import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress, 
  TextField, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  Button, 
  Menu,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  Chip,
  Alert,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  Refresh as RefreshIcon, 
  Analytics as AnalyticsIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import * as auditApi from '../../auditApi';
import { api } from '../../services/api';

// Theme-based color palette
const getThemeColors = (theme) => [
  theme.palette.primary.main,
  theme.palette.secondary.main,
  theme.palette.success.main,
  theme.palette.warning.main,
  theme.palette.error.main,
  theme.palette.info.main,
  theme.palette.primary.light,
  theme.palette.secondary.light,
  theme.palette.success.light,
  theme.palette.warning.light
];

const AuditLogPanel = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniqueActions, setUniqueActions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState('');
  
  // Date range filter state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Dashboard state
  const [dashboardData, setDashboardData] = useState({
    statistics: null,
    performanceMetrics: null,
    loading: true,
    error: null
  });
  
  useEffect(() => {
    fetchLogs();
    fetchDashboardData();
  }, []);
  
  useEffect(() => {
    // Filter logs based on search term and filters
    let filtered = logs;
    
    // Date range filtering
    if (startDate || endDate) {
      filtered = filtered.filter(log => {
        const logDate = new Date(log.event_timestamp || log.event_date_str);
        if (!logDate || isNaN(logDate.getTime())) return false;
        
        if (startDate && logDate < startDate) return false;
        if (endDate) {
          const endOfDay = new Date(endDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (logDate > endOfDay) return false;
        }
        return true;
      });
    }
    
    if (searchTerm) {
      filtered = filtered.filter(log => 
        (log.details?.page_name && log.details.page_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.details?.page_url && log.details.page_url.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.details?.campaign_name && log.details.campaign_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.details?.brand_name && log.details.brand_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.details?.action_name && log.details.action_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.details?.path && log.details.path.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.ip_address && log.ip_address.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.entity_type && log.entity_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.username && log.username.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action_type === actionFilter);
    }
    
    if (userFilter !== 'all') {
      filtered = filtered.filter(log => log.username === userFilter);
    }
    
    setFilteredLogs(filtered);
  }, [logs, searchTerm, actionFilter, userFilter, startDate, endDate]);
  
  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await auditApi.getAuditLogs();
      console.log('AuditLogPanel fetchLogs full response:', JSON.stringify(response, null, 2));
      console.log('Response keys:', Object.keys(response));
      console.log('Data keys:', response.data ? Object.keys(response.data) : 'No data');
      let logData = [];
      // Check if response directly contains results (not nested under data)
      if (response.results && Array.isArray(response.results)) {
        logData = response.results;
        console.log('Direct results array detected in response');
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        logData = response.data.results;
        console.log('Results array detected in response.data');
      } else if (response.data && Array.isArray(response.data)) {
        logData = response.data;
        console.log('Direct array detected in response.data');
      } else {
        console.error('Unexpected response structure:', response);
        setError('Failed to load audit logs: Unexpected data structure');
      }
      if (logData.length > 0) {
        setLogs(logData);
        setFilteredLogs(logData);
        // Extract unique users and actions for filters
        const users = new Set(logData.map(log => log.user_name || log.username || ''));
        const actions = new Set(logData.map(log => log.action || log.action_type || ''));
        setUniqueUsers([...users].filter(Boolean));
        setUniqueActions([...actions].filter(Boolean));
      } else {
        setLogs([]);
        setFilteredLogs([]);
        setError('Failed to load audit logs: No data available');
      }
    } catch (err) {
      console.error('AuditLogPanel fetchLogs error:', err);
      setError('Failed to load audit logs: ' + (err.message || 'Unknown error'));
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true, error: null }));
      
      // Fetch audit statistics
      const statsResponse = await api.get('/audit-logs/statistics/');
      console.log('Audit statistics response:', statsResponse);
      
      // Fetch performance metrics  
      const metricsResponse = await api.get('/audit-logs/performance_metrics/');
      console.log('Performance metrics response:', metricsResponse);
      
      setDashboardData({
        statistics: statsResponse.data || statsResponse,
        performanceMetrics: metricsResponse.data || metricsResponse,
        loading: false,
        error: null
      });
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setDashboardData(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Failed to load dashboard data: ' + (err.message || 'Unknown error')
      }));
    }
  };
  
  // Helper function to display entity information more meaningfully
  const formatEntityDisplay = (log) => {
    const entityType = log.entity_type || 'N/A';
    const entityId = log.entity_id;
    
    // For Campaign entities, try to show campaign name from details
    if (entityType === 'Campaign') {
      if (log.details?.campaign_name) {
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              📋 {log.details.campaign_name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Campaign
              {log.details?.brand_name && ` • ${log.details.brand_name}`}
            </Typography>
          </Box>
        );
      } else if (entityId) {
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              📋 Campaign ID: {entityId}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Campaign
            </Typography>
          </Box>
        );
      }
    }
    
    // For API entities
    if (entityType === 'API') {
      const path = log.details?.path || 'Unknown API';
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            🔗 {path}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            API Call
          </Typography>
        </Box>
      );
    }
    
    // For Page entities
    if (entityType === 'Page') {
      const pageName = log.details?.page_name || 'Unknown Page';
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            📄 {pageName}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Page View
          </Typography>
        </Box>
      );
    }
    
    // For User entities
    if (entityType === 'User') {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            👤 {log.username || 'User'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            User Action
          </Typography>
        </Box>
      );
    }
    
    // Default case - show entity type and ID if available
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {entityType}
        </Typography>
        {entityId && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ID: {entityId}
          </Typography>
        )}
      </Box>
    );
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const handleActionFilterChange = (event) => {
    setActionFilter(event.target.value);
  };
  
  const handleUserFilterChange = (event) => {
    setUserFilter(event.target.value);
  };

  const handleDateRangeChange = (type, date) => {
    if (type === 'start') {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const handleClearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleRefresh = () => {
    fetchLogs();
    fetchDashboardData();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // const handleFilterClick = (event, column) => {
  //   setAnchorEl(event.currentTarget);
  //   setFilterColumn(column);
  // };
  
  const handleFilterClose = () => {
    setAnchorEl(null);
    setFilterColumn('');
  };
  
  const handleFilterSelect = (value) => {
    if (filterColumn === 'action') {
      setActionFilter(value);
    } else if (filterColumn === 'user') {
      setUserFilter(value);
    }
    handleFilterClose();
  };

  // Dashboard components
  const StatCard = ({ title, value, icon, color = 'primary', subtitle = '', loading = false }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            mr: 2, 
            color: `${color}.main`,
            display: 'flex',
            alignItems: 'center'
          }}>
            {icon}
          </Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Typography variant="h4" component="div" color="text.primary" sx={{ mb: 1 }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  const ActionBreakdownChart = () => {
    const { statistics } = dashboardData;
    if (!statistics?.action_breakdown) return null;

    const themeColors = getThemeColors(theme);
    const data = statistics.action_breakdown.slice(0, 8).map(item => ({
      name: item.action_type || 'Unknown',
      value: item.count
    }));

    return (
      <ResponsiveContainer width="100%" height={350}>
        <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent, value }) => {
              if (percent < 5) return ''; // Hide labels for small slices
              return `${name}\n${(percent * 100).toFixed(0)}%`;
            }}
            outerRadius={90}
            innerRadius={30}
            fill={theme.palette.primary.main}
            dataKey="value"
            paddingAngle={2}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={themeColors[index % themeColors.length]} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value, name) => [value.toLocaleString(), name]}
            contentStyle={{ 
              backgroundColor: theme.palette.background.paper, 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '4px',
              color: theme.palette.text.primary
            }}
          />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            wrapperStyle={{ fontSize: '12px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const EntityBreakdownChart = () => {
    const { statistics } = dashboardData;
    if (!statistics?.entity_breakdown) return null;

    const data = statistics.entity_breakdown.slice(0, 10).map(item => ({
      name: item.entity_type || 'Unknown',
      shortName: (item.entity_type || 'Unknown').length > 12 ? 
        (item.entity_type || 'Unknown').substring(0, 12) + '...' : 
        (item.entity_type || 'Unknown'),
      count: item.count
    }));

    // Calculate better domain for small values
    const maxValue = Math.max(...data.map(d => d.count));
    const minDisplayValue = Math.max(maxValue * 0.8, 5);

    return (
      <ResponsiveContainer width="100%" height={350}>
        <BarChart 
          data={data} 
          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="shortName" 
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            tick={{ fontSize: 11 }}
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => value.toLocaleString()}
            domain={[0, Math.max(maxValue + 2, minDisplayValue)]}
          />
          <Tooltip 
            formatter={(value, name) => [value.toLocaleString(), 'Count']}
            labelFormatter={(label, payload) => payload?.[0]?.payload?.name || label}
            contentStyle={{ 
              backgroundColor: theme.palette.background.paper, 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '4px',
              color: theme.palette.text.primary
            }}
          />
          <Bar 
            dataKey="count" 
            fill={theme.palette.primary.main} 
            radius={[4, 4, 0, 0]}
            minPointSize={5}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

    const HourlyActivityChart = () => {
    const { statistics } = dashboardData;
    if (!statistics?.hourly_activity) return null;

    const data = statistics.hourly_activity.map(item => ({
      hour: `${item.hour}:00`,
      count: item.count
    }));

    // Calculate better domain for small values
    const maxValue = Math.max(...data.map(d => d.count));
    const minDisplayValue = Math.max(maxValue * 0.8, 3);

    return (
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart 
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="hour" 
            tick={{ fontSize: 11 }}
            interval={0}
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => value.toLocaleString()}
            domain={[0, Math.max(maxValue + 1, minDisplayValue)]}
          />
          <Tooltip 
            formatter={(value, name) => [value.toLocaleString(), 'Activities']}
            contentStyle={{ 
              backgroundColor: theme.palette.background.paper, 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '4px',
              color: theme.palette.text.primary
            }}
          />
          <Area 
            type="monotone" 
            dataKey="count" 
            stroke={theme.palette.primary.main} 
            fill={theme.palette.primary.main} 
            fillOpacity={0.3}
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const PageViewsChart = () => {
    // Extract page views from logs with more precise data extraction
    const pageViews = {};
    
    console.log('PageViewsChart - logs sample:', logs.slice(0, 3));
    
    // Filter logs to only include VIEW actions and page-related entities
    const pageViewLogs = filteredLogs.filter(log => 
      log.action_type === 'VIEW' && 
      (log.entity_type === 'Page' || log.details?.page_name || log.details?.page_url)
    );
    
    pageViewLogs.forEach(log => {
      let pageName = null;
      
      // Priority order for extracting page name
      if (log.details?.page_name && log.details.page_name !== 'N/A') {
        pageName = log.details.page_name;
      } else if (log.details?.page_url && log.details.page_url !== '/') {
        // Clean up URL paths for better display
        const urlPath = log.details.page_url;
        if (urlPath.startsWith('/')) {
          const pathParts = urlPath.split('/').filter(part => part);
          pageName = pathParts.length > 0 ? pathParts.join(' / ') : 'Home';
        } else {
          pageName = urlPath;
        }
      } else if (log.entity_type === 'Page') {
        pageName = 'Page View';
      }
      
      // Clean up and format page names
      if (pageName) {
        // Replace common technical terms with user-friendly names
        const friendlyNames = {
          'dashboard': 'Dashboard',
          'campaigns': 'Campaigns',
          'excel-view': 'Excel View',
          'settings': 'Settings',
          'audit-logs': 'Audit Logs',
          'total-data': 'Total Data',
          'versions': 'Version Management',
          'import-data': 'Data Import',
          'export-data': 'Data Export',
          'user-management': 'User Management',
          'categories': 'Categories'
        };
        
        const lowerPageName = pageName.toLowerCase();
        const friendlyName = friendlyNames[lowerPageName] || pageName;
        
        pageViews[friendlyName] = (pageViews[friendlyName] || 0) + 1;
      }
    });

    console.log('PageViewsChart - pageViews:', pageViews);

    const allData = Object.entries(pageViews)
      .sort(([,a], [,b]) => b - a)
      .map(([page, count]) => ({
        page: page.length > 25 ? page.substring(0, 25) + '...' : page,
        fullPage: page,
        count
      }));

    // If no data, show a more informative message
    if (allData.length === 0) {
      return (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                No page view data available
              </Typography>
              <Typography variant="body2">
                • This shows pages visited by users based on audit logs
              </Typography>
              <Typography variant="body2">
                • Only VIEW actions on page entities are counted
              </Typography>
              <Typography variant="body2">
                • Try adjusting the date range filter to see historical data
              </Typography>
            </Box>
          </Alert>
          <Box sx={{ 
            height: 200, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: theme.palette.action.hover
          }}>
            <Typography variant="h6" color="text.secondary">
              📊 No Page Views to Display
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        {/* Detailed Table - No Chart */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            📋 Page Views Ranking ({allData.length} pages total)
          </Typography>
          <TableContainer 
            component={Paper} 
            sx={{ 
              maxHeight: 400,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.background.default }}>
                    Rank
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.background.default }}>
                    Page Name
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ fontWeight: 'bold', bgcolor: theme.palette.background.default }}
                  >
                    Views
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ fontWeight: 'bold', bgcolor: theme.palette.background.default }}
                  >
                    % of Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allData.map((item, index) => {
                  const totalViews = allData.reduce((sum, d) => sum + d.count, 0);
                  const percentage = ((item.count / totalViews) * 100).toFixed(1);
                  const isTopThree = index < 3;
                  
                  return (
                    <TableRow 
                      key={index}
                      sx={{
                        '&:hover': { bgcolor: theme.palette.action.hover },
                        bgcolor: isTopThree ? theme.palette.action.selected : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {index > 2 && `${index + 1}.`}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          title={item.fullPage}
                          sx={{ 
                            fontWeight: isTopThree ? 'bold' : 'normal',
                            color: isTopThree ? theme.palette.primary.main : 'inherit'
                          }}
                        >
                          {item.fullPage}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.secondary.main 
                          }}
                        >
                          {item.count.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {percentage}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Summary Statistics */}
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: theme.palette.background.default, 
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Page Views
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {allData.reduce((sum, d) => sum + d.count, 0).toLocaleString()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Unique Pages
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {allData.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Avg Views per Page
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {allData.length > 0 ? 
                  Math.round(allData.reduce((sum, d) => sum + d.count, 0) / allData.length) : 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Date Range
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {startDate || endDate ? 
                  `${startDate ? startDate.toLocaleDateString() : 'All'} - ${endDate ? endDate.toLocaleDateString() : 'Now'}` :
                  'All Time'
                }
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const PerformanceMetrics = () => {
    const { performanceMetrics } = dashboardData;
    if (!performanceMetrics) return null;

    const tableStats = performanceMetrics.table_stats || {};
    const recentPerf = performanceMetrics.recent_performance || {};

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Storage Statistics
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Table Size
                </Typography>
                <Typography variant="h4">
                  {tableStats.size_mb || 0} MB
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Total Records
                </Typography>
                <Typography variant="h4">
                  {(tableStats.row_count || 0).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Performance Metrics
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Recent Query Time
                </Typography>
                <Typography variant="h4">
                  {((recentPerf.query_time_seconds || 0) * 1000).toFixed(0)} ms
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Recent Logs (24h)
                </Typography>
                <Typography variant="h4">
                  {(recentPerf.recent_logs_count || 0).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Dashboard tab content
  const DashboardContent = () => {
    const { statistics, loading, error } = dashboardData;

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    return (
      <Box>
        {/* Overview Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Logs"
              value={statistics?.total_logs || 0}
              icon={<AssessmentIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Unique Users"
              value={statistics?.unique_users || 0}
              icon={<PeopleIcon />}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Unique Agencies"
              value={statistics?.unique_agencies || 0}
              icon={<AnalyticsIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Period"
              value={statistics?.first_log && statistics?.last_log ? 
                `${Math.ceil((new Date(statistics.last_log) - new Date(statistics.first_log)) / (1000 * 60 * 60 * 24))} days` : 
                'N/A'
              }
              icon={<ScheduleIcon />}
              color="warning"
            />
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Actions Breakdown
                </Typography>
                <ActionBreakdownChart />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Entity Types
                </Typography>
                <EntityBreakdownChart />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Activity by Hour (Last 24h)
                </Typography>
                <HourlyActivityChart />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Most Viewed Pages
                </Typography>
                <PageViewsChart />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Performance Metrics */}
        <PerformanceMetrics />
      </Box>
    );
  };
      
  // Table content (existing table view)
  const TableContent = () => (
    <Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}
      
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField 
            placeholder="Search by campaign name, action, user, IP, path..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, minWidth: '200px' }}
          />
          
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => handleDateRangeChange('start', date)}
            slotProps={{ 
              textField: { 
                size: 'small', 
                sx: { minWidth: '140px' } 
              } 
            }}
          />
          
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => handleDateRangeChange('end', date)}
            slotProps={{ 
              textField: { 
                size: 'small', 
                sx: { minWidth: '140px' } 
              } 
            }}
          />
          
          {(startDate || endDate) && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearDateRange}
              startIcon={<DateRangeIcon />}
              sx={{ minWidth: '100px' }}
            >
              Clear Dates
            </Button>
          )}
          
          <FormControl sx={{ minWidth: '150px' }} size="small">
            <InputLabel>Action Type</InputLabel>
            <Select
              value={actionFilter}
              onChange={handleActionFilterChange}
              label="Action Type"
            >
              <MenuItem value="all">All Actions</MenuItem>
              {uniqueActions.map(action => (
                <MenuItem key={action} value={action}>{action}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: '150px' }} size="small">
            <InputLabel>User</InputLabel>
            <Select
              value={userFilter}
              onChange={handleUserFilterChange}
              label="User"
            >
              <MenuItem value="all">All Users</MenuItem>
              {uniqueUsers.map(user => (
                <MenuItem key={user} value={user}>{user}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </LocalizationProvider>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredLogs.length === 0 ? (
        <Typography>No audit logs found.</Typography>
      ) : (
        <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target/Entity</TableCell>
              <TableCell>IP Address</TableCell>
                <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
              {filteredLogs.map((log, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {log.event_timestamp ? new Date(log.event_timestamp).toLocaleString() : 'N/A'}
                </TableCell>
                  <TableCell>{log.user_name || log.username || 'N/A'}</TableCell>
                    <TableCell>
                    <Chip 
                      label={log.action || log.action_type || 'N/A'} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    </TableCell>
                  <TableCell>{formatEntityDisplay(log)}</TableCell>
                    <TableCell>{log.ip_address || 'N/A'}</TableCell>
                  <TableCell>
                    {log.details ? (
                      <Box>
                        {log.details.action_name && (
                          <Typography variant="body2">
                            <strong>Action:</strong> {log.details.action_name}
                          </Typography>
                        )}
                        {log.details.activity_type && (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            Type: {log.details.activity_type}
                          </Typography>
                        )}
                        {log.details.page_name && (
                          <Typography variant="body2">
                            <strong>Page:</strong> {log.details.page_name}
                          </Typography>
                        )}
                        {log.details.page_url && (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {log.details.page_url}
                          </Typography>
                        )}
                        {log.details.campaign_name && (
                          <Typography variant="body2">
                            <strong>Campaign:</strong> {log.details.campaign_name}
                          </Typography>
                        )}
                        {log.details.brand_name && (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            Brand: {log.details.brand_name}
                          </Typography>
                        )}
                        {log.details.save_type && (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            Save Type: {log.details.save_type}
                          </Typography>
                        )}
                        {log.details.method && (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            Method: {log.details.method}
                          </Typography>
                        )}
                        {log.details.path && (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            Path: {log.details.path}
                          </Typography>
                        )}
                        {log.details.error_message && (
                          <Typography variant="body2" sx={{ color: 'error.main' }}>
                            <strong>Error:</strong> {log.details.error_message}
                          </Typography>
                        )}
                        {/* Show any other details */}
                        {Object.keys(log.details).length > 0 && (
                          <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
                            {Object.entries(log.details)
                              .filter(([key]) => !['action_name', 'activity_type', 'page_name', 'page_url', 'campaign_name', 'brand_name', 'save_type', 'method', 'path', 'error_message'].includes(key))
                              .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
                              .join(' | ')
                            }
                          </Typography>
                        )}
                      </Box>
                    ) : 'N/A'}
                </TableCell>
              </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Box>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Audit Analytics</Typography>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading || dashboardData.loading}
        >
          Refresh
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab 
            label="Dashboard" 
            icon={<AnalyticsIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Logs Table" 
            icon={<TableChartIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {activeTab === 0 && <DashboardContent />}
      {activeTab === 1 && <TableContent />}
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleFilterClose}
      >
        {filterColumn === 'action' && uniqueActions.map(action => (
          <MenuItem key={action} onClick={() => handleFilterSelect(action)}>
            {action}
          </MenuItem>
        ))}
        {filterColumn === 'user' && uniqueUsers.map(user => (
          <MenuItem key={user} onClick={() => handleFilterSelect(user)}>
            {user}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default AuditLogPanel;
